import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatRadioModule } from '@angular/material/radio';
import { Router, RouterModule } from '@angular/router';
import { DialogComponent, DialogConfiguration, DialogService, ListViewComponent, TranslationPipe } from '@prekog/common-ui';
import { interval, Observable, Subscription, switchMap } from 'rxjs';
import { EmailTemplate } from '../model/email-template.model';
import { NewsletterTemplate } from '../model/newsletter-template.model';
import { EmailService } from '../template-name-translation.service';
import { ConfirmsubscribersDialogComponent } from './confirmsubscribersdialog/confirmsubscribersdialog.component';
import { ConfirmSubscribersDialogResult } from './confirmsubscribersdialog/confirmsubscribersdialogresult';
import { EmailQueueItem } from './model/email-queue-item.model';
import { FilterEmailTemplate } from './model/filter-email-template.model';
import { FilterTypeEnum } from './model/filter-type.enum';
import { PollingNewsletter } from './model/polling-newsletter.model';
import { DeleteNewsletterRequest } from './use-cases/delete-newsletter/delete-newsletter.request';
import { DeleteNewsletterUseCase } from './use-cases/delete-newsletter/delete-newsletter.usecase';
import { GetEmailTemplatesUseCase } from './use-cases/get-email-templates/get-email-templates.usecase';
import { InitializeNewsletterSendingRequest } from './use-cases/initialize-newsletter-sending/initialize-newsletter-sending.request';
import { InitializeNewsletterSendingUseCase } from './use-cases/initialize-newsletter-sending/initialize-newsletter-sending.usecase';
import { PollingNewsletterRequest } from './use-cases/polling-newsletter/polling-newsletter.request';
import { PollingNewsletterResponse } from './use-cases/polling-newsletter/polling-newsletter.response';
import { PollNewsletterUseCase } from './use-cases/polling-newsletter/polling-newsletter.usecase';

@Component({
    selector: 'app-email-template-list',
    templateUrl: './email-template-list.component.html',
    styleUrls: ['./email-template-list.component.scss'],
    standalone: true,
    imports: [MatRadioModule, MatIconModule, FormsModule, CommonModule, MatProgressBarModule, RouterModule, TranslationPipe, ListViewComponent],
})
export class EmailTemplateListComponent implements OnInit, OnDestroy {
    private _router = inject(Router);
    private _dialogService = inject(DialogService);
    private _emailService = inject(EmailService);
    private _getEmailTemplatesUseCase = inject(GetEmailTemplatesUseCase);
    private _initializeNewsletterSendingUseCase = inject(InitializeNewsletterSendingUseCase);
    private _deleteNewsletterUseCase = inject(DeleteNewsletterUseCase);
    private _pollNewsletterUseCase = inject(PollNewsletterUseCase);

    protected readonly ALL = FilterTypeEnum.ALL;
    protected readonly NEWSLETTER = FilterTypeEnum.NEWSLETTER;
    protected readonly SIMPLE = FilterTypeEnum.SIMPLE;
    protected readonly POLLING_INTERVAL_MS = 500;

    emailTemplateList = signal<EmailTemplate[]>([]);
    newsletterTemplateList = signal<NewsletterTemplate[]>([]);
    filteredEmailTemplateList = signal<FilterEmailTemplate[]>([]);
    filterType = signal<FilterTypeEnum>(FilterTypeEnum.ALL);
    progressColor: 'primary' | 'accent' | 'warn' = 'primary';
    searchText = signal<string>('');

    private _pollingSubscriptionList: { [key: string]: Subscription } = {};

    ngOnInit(): void {
        if (!sessionStorage.getItem('token')) {
            void this._router.navigateByUrl('/');
            return;
        }

        const response$ = this._getEmailTemplatesUseCase.execute() as Observable<any>;
        response$.subscribe((response) => {
            const newsletterTemplates = response.newsLetterTemplateList || [];

            newsletterTemplates.forEach((newsletter: NewsletterTemplate) => {
                if (newsletter.currentEmailQueueCount! > 0) {
                    this._startNewsletterPolling(newsletter.templateName!);
                }
            });

            this.emailTemplateList.set(response.emailTemplateList || []);
            this.newsletterTemplateList.set(newsletterTemplates);
            this.filterAll();
        });
    }

    ngOnDestroy(): void {
        this._stopAllPolling();
    }

    private _startNewsletterPolling(templateName: string): void {
        const pollingNewsletterRequest: PollingNewsletterRequest = {
            templateName: templateName,
        };

        this._pollingSubscriptionList[templateName] = interval(this.POLLING_INTERVAL_MS)
            .pipe(
                switchMap(() => this._pollNewsletterUseCase.execute(pollingNewsletterRequest) as Observable<PollingNewsletterResponse>)
            )
            .subscribe((response) => {
                const pollingNewsletter = response.pollingNewsletter as PollingNewsletter;

                this.newsletterTemplateList.update((list) =>
                    list.map((template) => {
                        if (template.templateName === templateName) {
                            return {
                                ...template,
                                currentEmailQueueCount: pollingNewsletter.currentEmailQueueCount,
                                alreadySent: pollingNewsletter.alreadySent,
                            };
                        }
                        return template;
                    })
                );

                this.filteredEmailTemplateList.update((list) =>
                    list.map((template) => {
                        if (template.templateName === templateName) {
                            return {
                                ...template,
                                currentEmailQueueCount: pollingNewsletter.currentEmailQueueCount,
                                alreadySent: pollingNewsletter.alreadySent,
                            };
                        }
                        return template;
                    })
                );

                const isJobDone = pollingNewsletter.currentEmailQueueCount === 0;
                if (isJobDone) {
                    this._stopPolling(templateName);
                }
            });
    }

    private _stopPolling(templateName: string): void {
        const subscription = this._pollingSubscriptionList[templateName];
        if (subscription) {
            subscription.unsubscribe();
            delete this._pollingSubscriptionList[templateName];
        }
    }

    private _stopAllPolling(): void {
        for (const templateName in this._pollingSubscriptionList) {
            if (this._pollingSubscriptionList.hasOwnProperty(templateName)) {
                this._pollingSubscriptionList[templateName].unsubscribe();
            }
        }
        this._pollingSubscriptionList = {};
    }

    getTranslatedName(emailTemplate: EmailTemplate): string {
        return this._emailService.getTranslatedName(emailTemplate);
    }

    filterAll(): void {
        const newsletters = this.newsletterTemplateList();
        const emails = this.emailTemplateList();
        this.filteredEmailTemplateList.set([...newsletters, ...(emails as FilterEmailTemplate[])]);
        this.filterType.set(FilterTypeEnum.ALL);
    }

    filterNewsLetter(): void {
        this.filteredEmailTemplateList.set([...this.newsletterTemplateList()]);
        this.filterType.set(FilterTypeEnum.NEWSLETTER);
    }

    filterSimpleEmails(): void {
        this.filteredEmailTemplateList.set([...this.emailTemplateList()]);
        this.filterType.set(FilterTypeEnum.SIMPLE);
    }

    routeToEditEmailTemplate(emailTemplate: EmailTemplate): void {
        this._router.navigate(['/editemail'], {
            state: {
                data: emailTemplate.templateName,
            },
        });
    }

    scheduleNewsLetter(newsletterTemplate: NewsletterTemplate, sendToNewRecipients: boolean): void {
        const emailQueueItem: EmailQueueItem = {
            templateId: newsletterTemplate.templateId,
            subject: newsletterTemplate.subject,
            message: newsletterTemplate.message,
        };
        const initializeNewsletterRequest: InitializeNewsletterSendingRequest = {
            emailQueueItem: emailQueueItem,
            onlyNewSubscribers: sendToNewRecipients,
        };

        const response$ = this._initializeNewsletterSendingUseCase.execute(initializeNewsletterRequest) as Observable<any>;
        response$.subscribe(() => {
            this._ensurePollingIsRunning(newsletterTemplate.templateName!);
        });
    }

    rescheduleNewsLetter(newsletterTemplate: NewsletterTemplate): void {
        const dialogconfiguration: DialogConfiguration = {
            targetData: newsletterTemplate.templateName
        }
        this._dialogService.openDialog(
            ConfirmsubscribersDialogComponent,
            dialogconfiguration,
            ((confirmSubscribersdialogResult: ConfirmSubscribersDialogResult | undefined) => {
                this.scheduleNewsLetter(newsletterTemplate, confirmSubscribersdialogResult?.sendToNewRecipients!);
            })
        )
    }

    openDeleteDialog(newsletterTemplateName: string): void {
        const dialogConfiguration: DialogConfiguration = {
            messageKey: 'emailTemplates.deleteConfirmMessage',
            targetData: newsletterTemplateName
        };

        this._dialogService.openDialog(
            DialogComponent,
            dialogConfiguration,
            () => {
                this._deleteNewsletter(newsletterTemplateName);
            });
    }

    private _ensurePollingIsRunning(templateName: string): void {
        if (!this._pollingSubscriptionList?.[templateName]) {
            this._startNewsletterPolling(templateName);
        }
    }

    private _deleteNewsletter(newsletterTemplateName: string): void {
        const deleteNewsletterRequest: DeleteNewsletterRequest = {
            templateName: newsletterTemplateName,
        };

        const response$ = this._deleteNewsletterUseCase.execute(deleteNewsletterRequest) as Observable<any>;
        response$.subscribe(() => {
            this.newsletterTemplateList.update((list) =>
                list.filter((template) => template.templateName !== newsletterTemplateName)
            );
            this.filteredEmailTemplateList.update((list) =>
                list.filter((template) => template.templateName !== newsletterTemplateName)
            );
        });
    }
}
